import { Fragment, useContext, useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { UNSAFE_DataRouterStateContext, useLocation } from "react-router-dom";
import { flexRender, type ColumnDef, type SortingState, type Table as ReactTableType } from "@tanstack/react-table";
import { PiArrowDownBold, PiArrowUpBold, PiArrowsDownUpBold, PiMagnifyingGlassDuotone } from "react-icons/pi";
import { Button, Flex, Table as RizzTable, Text } from "rizzui";
import { cn, EmptyState, MainTable, TablePagination, useTanStackTable, WidgetCard } from "@steelyard/theme";
import { DEFAULT_PAGE_SIZE } from "../../lib/paging";
import { TableSearch, TableToolbar } from "./toolbar";
import { RowPrimaryContext, type RowPrimary } from "./row-actions";
import { rememberListSearch, useReturned } from "../../lib/navigation";

/**
 * THE table every list in `apps/admin` renders through — `PagedTable` for the URL-paged lists, and
 * the report screens that fetch one bounded set and page it in memory. COPIED from
 * vendor/isomorphic/packages/isomorphic-core/src/components/table/index.tsx — `MainTable` itself,
 * with its own `TableHeadBasic` and default body-row branch handed back through its `components`
 * slots (minus the pinning/resizing branches this app never enables), and the template's
 * `TablePagination` under it. Three things are ours:
 *
 *  - a legible sort control: the whole label is the button, an unsorted column shows a muted two-way
 *    arrow (the template shows the same caret for "unsorted" and "descending"), the active direction
 *    is a primary arrow, and `aria-sort` carries it to a screen reader;
 *  - a per-row class and an UNCONDITIONAL sub-row. The template renders `expandedComponent` only
 *    when a reader toggles a row open; a BR-07 defect banner, a duplicate group's candidates and the
 *    tier-price editor are not things a reader opts into seeing;
 *  - the footer's "Showing X–Y of N". On a server-paged list N is the API's own `totalCount`, and a
 *    header sort that can only reorder the page on screen says so in words ("sorted within this
 *    page · Reset") — no service takes a sort parameter, and a control that silently reorders 10 of
 *    4,000 rows while looking list-wide is worse than none.
 *
 * While a loader refetches this same route (a filter, a page, a page size) the rows dim and the
 * table is `aria-busy` — no live region and no status role, which `data-table.test.tsx` pins. The
 * data router's state is read from its context rather than `useNavigation()`, which throws outside a
 * data router; a unit test's plain `MemoryRouter` has none, and there is nothing to dim there.
 *
 * A `table.setGlobalFilter` search that hides every loaded row says so, with its own way out,
 * instead of `MainTable`'s bare "No Data" — which would read like an empty list, and an empty list
 * and "your search matched nothing here" are different facts.
 */

/**
 * The Marketplace admin portal's grid (2026-09-28): the container owns the frame; a muted header band
 * of small, medium-weight, muted uppercase labels; the same 12px gutter on every cell; 11px row
 * padding; each row tinted on hover. Every table in the app wears it — `TableView` here, and the
 * detail screens' line tables (an order's, an invoice's, a credit note's) on their rizzui `Table`.
 */
export const GRID_TABLE = cn(
  "[&_thead]:bg-gray-100/60 [&_th]:px-3 [&_th]:py-2 [&_th]:text-xs [&_th]:font-medium [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-gray-500",
  "[&_td]:px-3 [&_td]:py-[11px] [&_td]:text-sm [&_tbody_tr:hover]:bg-gray-100",
);

const sortedWithinPageTitle = (serverPaged: boolean) =>
  serverPaged ? "Sorts the rows on this page — the list itself is ordered by the server" : undefined;

export function TableView<T extends Record<string, unknown>>({
  table,
  caption,
  total,
  serverPaged = false,
  loading = false,
  subRow,
  rowClassName,
}: {
  table: ReactTableType<T>;
  /** Screen-reader name for the table. */
  caption?: string;
  /** The API's `totalCount` on a server-paged list. Otherwise the rows left after any search. */
  total?: number;
  serverPaged?: boolean;
  /** A screen that fetches in an effect (the reports) says when it is re-running. */
  loading?: boolean;
  /** A full-width row after a given row - an inline defect banner, a duplicate group's candidates. */
  subRow?: (row: T) => ReactNode;
  /** Emphasis on a row the API flagged - e.g. a BR-07 invoice defect. Never computed here. */
  rowClassName?: (row: T) => string | undefined;
}) {
  const navigation = useContext(UNSAFE_DataRouterStateContext)?.navigation;
  const { pathname } = useLocation();
  const busy = loading || (navigation?.state === "loading" && navigation.location?.pathname === pathname);

  const shownRows = table.getRowModel().rows.length;
  const { pageIndex, pageSize } = table.getState().pagination;
  const matching = total ?? table.getFilteredRowModel().rows.length;
  const firstShown = shownRows === 0 ? 0 : pageIndex * pageSize + 1;
  const lastShown = shownRows === 0 ? 0 : firstShown + shownRows - 1;
  const sortedWithinPage = serverPaged && table.getState().sorting.length > 0;

  const searchTerm = table.getState().globalFilter as string | undefined;
  const loaded = table.getCoreRowModel().rows.length;
  if (searchTerm && loaded > 0 && table.getFilteredRowModel().rows.length === 0) {
    return (
      <div className="p-4">
        <EmptyState
          title="No rows match your search"
          icon={<PiMagnifyingGlassDuotone aria-hidden className="size-10 text-gray-400" />}
          action={
            <Button size="sm" variant="outline" onClick={() => table.setGlobalFilter("")}>
              Clear the search
            </Button>
          }
        >
          None of the {loaded} rows loaded here match &ldquo;{searchTerm}&rdquo;.
        </EmptyState>
      </div>
    );
  }

  return (
    <div aria-busy={busy || undefined} className={cn("@container transition-opacity", busy && "pointer-events-none opacity-60")}>
      {caption ? <span className="sr-only">{caption}</span> : null}
      <MainTable
        table={table}
        variant="modern"
        classNames={{
          rowClassName: "last:border-0",
          tableClassName: cn("!w-full [&_td_.rounded-full]:whitespace-nowrap", GRID_TABLE),
        }}
        components={{
          headerCell: ({ headerGroup }) => (
            <>
              {headerGroup?.headers.map((header) => {
                const sorted = header.column.getIsSorted();
                const right = header.column.columnDef.meta?.align === "right";
                const label = header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext());
                return (
                  <RizzTable.Head
                    key={header.id}
                    colSpan={header.colSpan}
                    // A share of the card, not pixels: the template sums the sizes into the table's
                    // own width, so eight sized columns scrolled sideways even at 1440px. Content that
                    // cannot wrap still widens the table, and the container scrolls then, on a phone.
                    // The row-actions column is one `⋮` button: as narrow as that, never a share.
                    style={{ width: isRowMenu(header.column.columnDef) ? "1%" : `${(header.getSize() / table.getTotalSize()) * 100}%` }}
                    aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : header.column.getCanSort() ? "none" : undefined}
                  >
                    {/* A flex row, not `text-end`: rizzui's head cell sets its own text alignment. */}
                    <Flex align="center" justify={right ? "end" : "start"} gap="1">
                      {header.column.getCanSort() ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            header.column.getToggleSortingHandler()?.(event);
                            // A new order of a list paged here starts on its first page.
                            if (!table.options.manualPagination) table.setPageIndex(0);
                          }}
                          title={sortedWithinPageTitle(serverPaged)}
                          className={cn(
                            "group inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-gray-900",
                            right && "flex-row-reverse",
                            sorted && "text-gray-900",
                          )}
                        >
                          {label}
                          {sorted === "asc" ? (
                            <PiArrowUpBold aria-hidden className="size-3 text-primary" />
                          ) : sorted === "desc" ? (
                            <PiArrowDownBold aria-hidden className="size-3 text-primary" />
                          ) : (
                            <PiArrowsDownUpBold aria-hidden className="size-3 opacity-60 group-hover:opacity-100" />
                          )}
                        </button>
                      ) : (
                        label
                      )}
                    </Flex>
                  </RizzTable.Head>
                );
              })}
            </>
          ),
          bodyRow: ({ table: t }) => (
            <>
              {(t ?? table).getRowModel().rows.map((row) => {
                const extra = subRow?.(row.original);
                return (
                  <Fragment key={row.id}>
                    <ClickableRow className={cn("transition-colors", extra ? "!border-b-0" : "last:border-0", rowClassName?.(row.original))}>
                      {row.getVisibleCells().map((cell) => (
                        <RizzTable.Cell
                          key={cell.id}
                          className={cn(cell.column.columnDef.meta?.align === "right" && "text-end tabular-nums")}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </RizzTable.Cell>
                      ))}
                    </ClickableRow>
                    {extra ? (
                      <RizzTable.Row className={rowClassName?.(row.original)}>
                        <RizzTable.Cell colSpan={row.getVisibleCells().length} className="!pt-0">
                          {extra}
                        </RizzTable.Cell>
                      </RizzTable.Row>
                    ) : null}
                  </Fragment>
                );
              })}
            </>
          ),
        }}
      />

      <div className="flex flex-col gap-3 border-t border-muted px-3 py-2 text-sm @2xl:flex-row @2xl:items-center @2xl:justify-between">
        <Text className="shrink-0 text-sm text-gray-500">
          Showing {firstShown}–{lastShown} of {matching}
          {sortedWithinPage ? (
            <>
              {" · sorted within this page "}
              <button type="button" onClick={() => table.resetSorting()} className="text-primary underline">
                Reset
              </button>
            </>
          ) : null}
        </Text>
        {/* `grow`, never `w-auto`: TablePagination is an `@container` (inline-size containment),
            which sized to its content collapses to zero width and hides the pager. */}
        <TablePagination table={table} className="min-w-0 grow" />
      </div>
    </div>
  );
}

/**
 * A whole result the screen already fetched — a report section, the GST summary, a credit note's
 * lines — in the same card, toolbar and table as every list. Paged in memory, with its page size
 * its own rather than the URL's: a report with five sections would otherwise re-slice all five from
 * one select. Never `PagedTable`, because these screens export, and `export-guard.test.ts` keeps an
 * export away from a server-paged table; the export maps the full fetched array, not this page.
 *
 * `useTanStackTable` seeds its rows once, at mount, so a new fetch re-seeds them through the hook's
 * own `setData`, the same way `PagedTable` does.
 */
/** The link a row with no `⋮` primary falls back to: its first same-tab, non-download link. */
const ROW_LINK = "a[href]:not([target=_blank]):not([download])";

/** Anything in a row that is its own control — a click there is that control's, not the row's. */
const OWN_CONTROL = "a, button, input, select, textarea, label, [role=button], [role=checkbox], [role=combobox], [role=listbox], [role=option], [role=menu], [role=menuitem]";

/**
 * A body row that opens its own primary action (View / Edit) on a click anywhere that is not
 * already a control (2026-09-29, the project owner's request). The action is the one the row's
 * `RowActions` registers in `RowPrimaryContext` — see ./row-actions.tsx — so a row with no menu, or
 * a menu with no primary item, is an ordinary row. Ctrl/Cmd-click opens a route in a new tab; a
 * click that ends a text selection does nothing; and a click inside the `⋮` menu, which React
 * bubbles here through its portal, is ignored because the menu is not inside the row's DOM.
 *
 * **A row with no `⋮` primary opens its first link instead** (2026-09-29: the packing, report,
 * audit and error-log grids have no row menu, only a record link in a cell — an order number, a
 * SKU). That link is already the row's way to its record, so a click elsewhere on the row follows
 * it; a row with neither stays an ordinary row. A new-tab or download link is never the fallback.
 *
 * The keyboard path is unchanged: the `⋮` button or the link is the row's focusable control, and
 * does the same thing.
 */
function ClickableRow({ className, children }: { className?: string; children: ReactNode }) {
  const primary = useRef<RowPrimary | null>(null);

  const onClick = (event: MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as Element;
    if (event.defaultPrevented || !event.currentTarget.contains(target)) return;
    if (target.closest(OWN_CONTROL)) return;
    if (window.getSelection()?.toString()) return;
    const newTab = event.metaKey || event.ctrlKey;

    const action = primary.current;
    if (action) {
      if (action.href && newTab) window.open(action.href, "_blank", "noopener");
      else action.run();
      return;
    }

    // Following the link through its own click keeps it a router navigation; that click lands back
    // here on an `a`, which OWN_CONTROL ignores, so it cannot loop.
    const link = event.currentTarget.querySelector<HTMLAnchorElement>(ROW_LINK);
    if (!link) return;
    if (newTab) window.open(link.href, "_blank", "noopener");
    else link.click();
  };

  return (
    <RowPrimaryContext.Provider value={primary}>
      <RizzTable.Row
        className={cn(className, "[&:has([data-row-primary])]:cursor-pointer [&:has(a[href]:not([target=_blank]))]:cursor-pointer")}
        onClick={onClick}
      >
        {children}
      </RizzTable.Row>
    </RowPrimaryContext.Provider>
  );
}

export function ReportTable<T extends Record<string, unknown>>({
  rows,
  columns,
  caption,
  title,
  description,
  action,
  searchPlaceholder,
  getRowId,
}: {
  rows: T[];
  columns: ColumnDef<T>[];
  caption: string;
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  /** Shows the template's search box over these rows — only where a column has something to match. */
  searchPlaceholder?: string;
  getRowId?: (row: T, index: number) => string;
}) {
  const { table, setData } = useTanStackTable<T>({
    tableData: rows,
    columnConfig: actionsFirst(columns),
    options: {
      getRowId,
      enableColumnResizing: false,
      autoResetPageIndex: false,
      initialState: { pagination: { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE } },
    },
  });
  const memory = useTableMemory(table, caption);
  const restored = useRef(memory);

  useEffect(() => {
    setData([...rows]);
    // A report fetches after it mounts: a returned-to one gets its search, sort and page back when
    // its rows arrive. Any other new set of rows starts on its first page.
    const back = restored.current;
    if (back && rows.length === 0) return;
    restored.current = undefined;
    if (back) restoreTableState(table, back);
    table.setPageIndex(back?.pageIndex ?? 0);
  }, [rows, setData, table]);

  return (
    <TableFrame
      title={title}
      description={description}
      action={action}
      toolbar={(inset) => (
        <TableToolbar
          table={table}
          inset={inset}
          search={searchPlaceholder ? <TableSearch table={table} placeholder={searchPlaceholder} /> : undefined}
        />
      )}
    >
      <TableView table={table} caption={caption} />
    </TableFrame>
  );
}

type TableMemory = { globalFilter?: string; sorting: SortingState; pageIndex: number };
const tableMemory = new Map<string, TableMemory>();

/**
 * What a list holds outside its URL — the search box over loaded rows, the page of a list paged in
 * memory, the sort — kept per URL and table, and handed back when that list is returned to (Back,
 * or a link from one of its own records; `useReturned`). The URL already holds every server filter
 * and the server page; this also records that URL as the list's last query, for `listReturn`.
 * A table that uses it sets `autoResetPageIndex: false`, or TanStack's own reset on the first rows
 * would throw the restored page away; it resets the page itself where a new set of rows arrives.
 */
export function useTableMemory<T>(table: ReactTableType<T>, id = ""): TableMemory | undefined {
  const { pathname, search } = useLocation();
  const key = `${pathname}${search}#${id}`;
  const returned = useReturned();
  const [restored] = useState(() => (returned ? tableMemory.get(key) : undefined));
  const { globalFilter, sorting, pagination } = table.getState();
  useEffect(() => {
    tableMemory.set(key, { globalFilter, sorting, pageIndex: pagination.pageIndex });
    rememberListSearch(pathname, search);
  }, [key, pathname, search, globalFilter, sorting, pagination.pageIndex]);
  return restored;
}

/** A restored search and sort, put back on the table. */
export function restoreTableState<T>(table: ReactTableType<T>, memory: TableMemory): void {
  if (memory.globalFilter) table.setGlobalFilter(memory.globalFilter);
  if (memory.sorting.length > 0) table.setSorting(memory.sorting);
}

/**
 * The row-actions column (`id: "actions"`) first, the way the Marketplace admin portal's grids put
 * their row menu at the start of every row — so it sits in the same place whatever else a screen's
 * columns hold. Every other column keeps the order its screen gave it.
 */
export function actionsFirst<T>(columns: ColumnDef<T, unknown>[]): ColumnDef<T, unknown>[] {
  const actions = columns.filter(isRowMenu);
  return actions.length === 0 ? columns : [...actions, ...columns.filter((column) => !isRowMenu(column))];
}

/** The unlabelled row-menu column — not a data column that happens to be id'd "actions" (packing's "Assignment"). */
function isRowMenu<T>(column: ColumnDef<T, unknown>) {
  return column.id === "actions" && !column.header;
}

/**
 * Where a list's toolbar and table sit. With no heading of its own (every list screen: its
 * `ListHeader` names it), the toolbar is flush on the page and the table below it in a plain bordered
 * frame — the Marketplace admin portal's grid. A titled table (a report section, a table inside a
 * detail page) keeps the template's `WidgetCard`, with the toolbar inset under its heading so the
 * two stay one unit. `toolbar` is told which, for its gutter.
 */
export function TableFrame({
  title,
  description,
  action,
  toolbar,
  children,
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  toolbar: (inset: boolean) => ReactNode;
  children: ReactNode;
}) {
  if (!title && !description && !action) {
    return (
      <div className="flex flex-col gap-4">
        {toolbar(false)}
        <div className="rounded-md border border-muted bg-gray-0 dark:bg-gray-50">{children}</div>
      </div>
    );
  }
  return (
    <WidgetCard
      title={title}
      description={description}
      action={action}
      rounded="DEFAULT"
      className="p-0 lg:p-0"
      headerClassName="border-b border-muted px-4 py-4 lg:px-6"
    >
      {toolbar(true)}
      {children}
    </WidgetCard>
  );
}
